import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { ProjectionResult } from '~/domain/services/orthographicProjection';

type DragTarget = 'zTop' | 'zBottom' | 'fovLeft' | 'fovRight';

const HANDLE_SIZE = 8;
const HIT_TOLERANCE = 12;
const MIN_Z_GAP_MM = 2;
const MIN_FOV_DRAG_MM = 50;

const Z_COLOR = 'rgba(0, 200, 255, 0.9)';
const EXCLUDED_FILL = 'rgba(0, 0, 0, 0.45)';

/** Draw a small filled triangle handle at a canvas edge. */
function drawHandle(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    direction: 'up' | 'down' | 'left' | 'right',
    color: string
): void {
    const s = HANDLE_SIZE;
    ctx.fillStyle = color;
    ctx.beginPath();
    switch (direction) {
        case 'right':
            ctx.moveTo(x, y - s);
            ctx.lineTo(x + s * 1.5, y);
            ctx.lineTo(x, y + s);
            break;
        case 'left':
            ctx.moveTo(x, y - s);
            ctx.lineTo(x - s * 1.5, y);
            ctx.lineTo(x, y + s);
            break;
        case 'down':
            ctx.moveTo(x - s, y);
            ctx.lineTo(x, y + s * 1.5);
            ctx.lineTo(x + s, y);
            break;
        case 'up':
            ctx.moveTo(x - s, y);
            ctx.lineTo(x, y - s * 1.5);
            ctx.lineTo(x + s, y);
            break;
    }
    ctx.closePath();
    ctx.fill();
}

interface ProjectionCanvasProps {
    projection: ProjectionResult | null;
    label: string;
    axis?: 'coronal' | 'sagittal';
    zRange?: [number, number];
    fovMm?: number;
    maxSize?: number;
    onZRangeChange?: (newRange: [number, number]) => void;
    onFovChange?: (newFov: number) => void;
}

export default function ProjectionCanvas({
    projection,
    label,
    axis,
    zRange,
    fovMm,
    maxSize = 150,
    onZRangeChange,
    onFovChange,
}: ProjectionCanvasProps) {
    const theme = useTheme();
    const fovColor = theme.palette.secondary.dark;

    const imageCanvasRef = useRef<HTMLCanvasElement>(null);
    const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
    const [cursor, setCursor] = useState('default');

    // Layout: image area within the maxSize × maxSize container
    const layout = useMemo(() => {
        let dw = maxSize;
        let dh = maxSize;
        if (projection) {
            const aspect = projection.widthPx / projection.heightPx;
            if (aspect > 1) {
                dh = Math.round(maxSize / aspect);
            } else {
                dw = Math.round(maxSize * aspect);
            }
        }
        return {
            dw,
            dh,
            ox: (maxSize - dw) / 2,
            oy: (maxSize - dh) / 2,
        };
    }, [projection, maxSize]);

    // --- Coordinate mapping helpers ---------------------------------

    const worldZToY = useCallback(
        (wz: number): number => {
            if (!projection) {
                return 0;
            }
            const ext = projection.worldMax[2] - projection.worldMin[2];
            if (ext <= 0) {
                return layout.oy;
            }
            return (
                layout.oy + ((projection.worldMax[2] - wz) / ext) * layout.dh
            );
        },
        [projection, layout]
    );

    const yToWorldZ = useCallback(
        (cy: number): number => {
            if (!projection) {
                return 0;
            }
            const ext = projection.worldMax[2] - projection.worldMin[2];
            return (
                projection.worldMax[2] - ((cy - layout.oy) / layout.dh) * ext
            );
        },
        [projection, layout]
    );

    const fovHalfToX = useCallback(
        (halfFov: number, side: 'left' | 'right'): number => {
            if (!projection || !axis) {
                return 0;
            }
            const i = axis === 'coronal' ? 0 : 1;
            const wMin = projection.worldMin[i] as number;
            const wMax = projection.worldMax[i] as number;
            const center = (wMin + wMax) / 2;
            const ext = wMax - wMin;
            if (ext <= 0) {
                return layout.ox;
            }
            const w = side === 'left' ? center - halfFov : center + halfFov;
            return layout.ox + ((w - wMin) / ext) * layout.dw;
        },
        [projection, axis, layout]
    );

    const xToFov = useCallback(
        (cx: number): number => {
            if (!projection || !axis) {
                return 0;
            }
            const i = axis === 'coronal' ? 0 : 1;
            const wMin = projection.worldMin[i] as number;
            const wMax = projection.worldMax[i] as number;
            const center = (wMin + wMax) / 2;
            const ext = wMax - wMin;
            const w = wMin + ((cx - layout.ox) / layout.dw) * ext;
            return Math.abs(w - center) * 2;
        },
        [projection, axis, layout]
    );

    // --- Draw MIP image (bottom canvas) ----------------------------

    useEffect(() => {
        const canvas = imageCanvasRef.current;
        if (!canvas || !projection) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        canvas.width = maxSize;
        canvas.height = maxSize;

        const tmp = document.createElement('canvas');
        tmp.width = projection.widthPx;
        tmp.height = projection.heightPx;
        const tmpCtx = tmp.getContext('2d');
        if (tmpCtx) {
            tmpCtx.putImageData(projection.imageData, 0, 0);
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(tmp, layout.ox, layout.oy, layout.dw, layout.dh);
        }
    }, [projection, maxSize, layout]);

    // --- Draw overlay (top canvas) ---------------------------------

    useEffect(() => {
        const canvas = overlayCanvasRef.current;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (!projection) {
            return;
        }

        const hasZ =
            zRange != null && projection.worldMax[2] !== projection.worldMin[2];
        const hasFov = fovMm != null && fovMm > 0 && axis != null;

        // Compute line positions in container coordinates
        const zTopY = hasZ ? worldZToY(zRange[1]) : 0;
        const zBotY = hasZ ? worldZToY(zRange[0]) : maxSize;
        const fLX = hasFov ? fovHalfToX(fovMm / 2, 'left') : 0;
        const fRX = hasFov ? fovHalfToX(fovMm / 2, 'right') : maxSize;

        // Dark fill on excluded regions
        ctx.fillStyle = EXCLUDED_FILL;
        if (hasZ) {
            if (zTopY > 0) {
                ctx.fillRect(0, 0, maxSize, zTopY);
            }
            if (zBotY < maxSize) {
                ctx.fillRect(0, zBotY, maxSize, maxSize - zBotY);
            }
        }
        if (hasFov) {
            const yT = hasZ ? Math.max(0, zTopY) : 0;
            const yB = hasZ ? Math.min(maxSize, zBotY) : maxSize;
            const h = yB - yT;
            if (fLX > 0) {
                ctx.fillRect(0, yT, fLX, h);
            }
            if (fRX < maxSize) {
                ctx.fillRect(fRX, yT, maxSize - fRX, h);
            }
        }

        // Z-range lines + handles
        if (hasZ) {
            ctx.strokeStyle = Z_COLOR;
            ctx.lineWidth = 2;

            ctx.beginPath();
            ctx.moveTo(0, zTopY);
            ctx.lineTo(maxSize, zTopY);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(0, zBotY);
            ctx.lineTo(maxSize, zBotY);
            ctx.stroke();

            if (onZRangeChange) {
                drawHandle(ctx, 0, zTopY, 'right', Z_COLOR);
                drawHandle(ctx, maxSize, zTopY, 'left', Z_COLOR);
                drawHandle(ctx, 0, zBotY, 'right', Z_COLOR);
                drawHandle(ctx, maxSize, zBotY, 'left', Z_COLOR);
            }
        }

        // FOV lines + handles
        if (hasFov) {
            ctx.strokeStyle = fovColor;
            ctx.lineWidth = 2;

            ctx.beginPath();
            ctx.moveTo(fLX, 0);
            ctx.lineTo(fLX, maxSize);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(fRX, 0);
            ctx.lineTo(fRX, maxSize);
            ctx.stroke();

            if (onFovChange) {
                drawHandle(ctx, fLX, 0, 'down', fovColor);
                drawHandle(ctx, fLX, maxSize, 'up', fovColor);
                drawHandle(ctx, fRX, 0, 'down', fovColor);
                drawHandle(ctx, fRX, maxSize, 'up', fovColor);
            }
        }
    }, [
        projection,
        maxSize,
        zRange,
        fovMm,
        axis,
        worldZToY,
        fovHalfToX,
        onZRangeChange,
        onFovChange,
        fovColor,
    ]);

    // --- Mouse helpers ---------------------------------------------

    const getPos = useCallback(
        (e: MouseEvent | React.MouseEvent): { x: number; y: number } => {
            const el = overlayCanvasRef.current;
            if (!el) {
                return { x: 0, y: 0 };
            }
            const rect = el.getBoundingClientRect();
            return {
                x: ((e.clientX - rect.left) / rect.width) * maxSize,
                y: ((e.clientY - rect.top) / rect.height) * maxSize,
            };
        },
        [maxSize]
    );

    const hitTest = useCallback(
        (x: number, y: number): DragTarget | null => {
            if (!projection) {
                return null;
            }

            const hasZ =
                zRange != null &&
                projection.worldMax[2] !== projection.worldMin[2] &&
                onZRangeChange != null;
            const hasFov =
                fovMm != null &&
                fovMm > 0 &&
                axis != null &&
                onFovChange != null;

            if (hasZ) {
                const zt = worldZToY(zRange[1]);
                const zb = worldZToY(zRange[0]);
                if (Math.abs(y - zt) < HIT_TOLERANCE) {
                    return 'zTop';
                }
                if (Math.abs(y - zb) < HIT_TOLERANCE) {
                    return 'zBottom';
                }
            }
            if (hasFov) {
                const fl = fovHalfToX(fovMm / 2, 'left');
                const fr = fovHalfToX(fovMm / 2, 'right');
                if (Math.abs(x - fl) < HIT_TOLERANCE) {
                    return 'fovLeft';
                }
                if (Math.abs(x - fr) < HIT_TOLERANCE) {
                    return 'fovRight';
                }
            }
            return null;
        },
        [
            projection,
            zRange,
            fovMm,
            axis,
            onZRangeChange,
            onFovChange,
            worldZToY,
            fovHalfToX,
        ]
    );

    // --- Mouse event handlers --------------------------------------

    const handleHover = useCallback(
        (e: React.MouseEvent) => {
            const p = getPos(e);
            const t = hitTest(p.x, p.y);
            if (t === 'zTop' || t === 'zBottom') {
                setCursor('ns-resize');
            } else if (t === 'fovLeft' || t === 'fovRight') {
                setCursor('ew-resize');
            } else {
                setCursor('default');
            }
        },
        [getPos, hitTest]
    );

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            const p = getPos(e);
            const target = hitTest(p.x, p.y);
            if (!target || !projection) {
                return;
            }

            e.preventDefault();

            // Capture fixed ends at drag start
            const fixedZMin = zRange ? zRange[0] : 0;
            const fixedZMax = zRange ? zRange[1] : 0;
            const wZMin = projection.worldMin[2] as number;
            const wZMax = projection.worldMax[2] as number;
            const hIdx = axis === 'coronal' ? 0 : 1;
            const wHMax =
                (projection.worldMax[hIdx] as number) -
                (projection.worldMin[hIdx] as number);

            const onMove = (me: MouseEvent) => {
                const mp = getPos(me);
                switch (target) {
                    case 'zTop': {
                        const z = Math.min(
                            wZMax,
                            Math.max(fixedZMin + MIN_Z_GAP_MM, yToWorldZ(mp.y))
                        );
                        onZRangeChange?.([fixedZMin, z]);
                        break;
                    }
                    case 'zBottom': {
                        const z = Math.max(
                            wZMin,
                            Math.min(fixedZMax - MIN_Z_GAP_MM, yToWorldZ(mp.y))
                        );
                        onZRangeChange?.([z, fixedZMax]);
                        break;
                    }
                    case 'fovLeft':
                    case 'fovRight': {
                        const f = Math.max(
                            MIN_FOV_DRAG_MM,
                            Math.min(wHMax, xToFov(mp.x))
                        );
                        onFovChange?.(Math.round(f));
                        break;
                    }
                }
            };

            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            };

            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        [
            getPos,
            hitTest,
            projection,
            zRange,
            axis,
            yToWorldZ,
            xToFov,
            onZRangeChange,
            onFovChange,
        ]
    );

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={0.5}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: maxSize,
                    height: maxSize,
                    flexShrink: 0,
                    backgroundColor: '#000',
                    borderRadius: 1,
                    overflow: 'hidden',
                }}
            >
                <canvas
                    ref={imageCanvasRef}
                    width={maxSize}
                    height={maxSize}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        imageRendering: 'pixelated',
                        pointerEvents: 'none',
                    }}
                />
                <canvas
                    ref={overlayCanvasRef}
                    width={maxSize}
                    height={maxSize}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        cursor,
                    }}
                    onMouseMove={handleHover}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={() => setCursor('default')}
                />
            </Box>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
        </Box>
    );
}
